// @catalog F.010
/**
 * use-forecast-popovers — extrae state + handlers de popovers + RowAdapter del
 * orchestrator ForecastPage.tsx para mantener P7 (<500 LoC).
 *
 * Provee:
 *   - popover state ({type, productId, anchorRect} | null) + setPopover/clearPopover.
 *   - openInventoryPopover / openSalesPopover (extraen anchorRect del evento).
 */
import { useCallback, useState } from "react";
import type { AnchorRect } from "./popover-shell";

export type PopoverState =
  | {
      type: "inventory" | "sales";
      productId: string;
      anchorRect: AnchorRect;
    }
  | null;

export interface UseForecastPopoversResult {
  popover: PopoverState;
  setPopover: (next: PopoverState) => void;
  clearPopover: () => void;
  openInventoryPopover: (
    e: React.MouseEvent<HTMLElement>,
    productId: string,
  ) => void;
  openSalesPopover: (
    e: React.MouseEvent<HTMLElement>,
    productId: string,
  ) => void;
}

function rectFromEvent(e: React.MouseEvent<HTMLElement>): AnchorRect {
  const r = e.currentTarget.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
}

export function useForecastPopovers(): UseForecastPopoversResult {
  const [popover, setPopover] = useState<PopoverState>(null);
  const clearPopover = useCallback(() => setPopover(null), []);
  const openInventoryPopover = useCallback(
    (e: React.MouseEvent<HTMLElement>, productId: string) =>
      setPopover({ type: "inventory", productId, anchorRect: rectFromEvent(e) }),
    [],
  );
  const openSalesPopover = useCallback(
    (e: React.MouseEvent<HTMLElement>, productId: string) =>
      setPopover({ type: "sales", productId, anchorRect: rectFromEvent(e) }),
    [],
  );
  return { popover, setPopover, clearPopover, openInventoryPopover, openSalesPopover };
}
